import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import FoundationParamsView from "./FoundationParamsView";
import FoundationTaxConfirm from "./FoundationTaxConfirm";
import FoundationNewCustomerNotice from "./FoundationNewCustomerNotice";

// Confirmation before the single sp_insert_update_jobs call. `preview`
// carries the post-truncation description so the user sees exactly what
// Foundation will receive, plus the Confirm-Tax-No step and the
// new-customer flag.
export default function FoundationSpConfirmDialog({
  open, onOpenChange, preview, onConfirm, testIdPrefix = "sp",
  taxNo, onTaxNoChange, onCustomerChanged, onPhase1,
}) {
  if (!preview) return null;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`${testIdPrefix}-confirm-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Push job to Foundation?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-slate-600">
              <p data-testid={`${testIdPrefix}-confirm-text`}>
                This will create or update job <strong>{preview.job_no}</strong> in Foundation
                (<code>{preview.database}</code>) with description &quot;
                <strong>{preview.description}</strong>&quot;. Proceed?
              </p>
              {preview.truncated === true && (
                <p className="text-amber-700 text-xs" data-testid={`${testIdPrefix}-confirm-truncated`}>
                  Description was longer than 30 characters and has been truncated to the value shown.
                </p>
              )}
              <div className="text-xs space-y-2">
                <FoundationNewCustomerNotice customer={preview.customer} onChanged={onCustomerChanged}
                                             testIdPrefix={testIdPrefix} />
                <FoundationTaxConfirm tax={preview.tax} value={taxNo} onChange={onTaxNoChange}
                                      testIdPrefix={testIdPrefix} />
              </div>
              {preview.params && (
                <FoundationParamsView params={preview.to_send || preview.params} withheld={preview.withheld}
                                      blockers={preview.blockers} truncated={Array.isArray(preview.truncated) ? preview.truncated : []}
                                      omittedUnchanged={preview.omitted_unchanged} database={preview.database}
                                      isUpdate={preview.is_update} testIdPrefix={testIdPrefix} />
              )}
              {preview.phase1_fallback_available && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" data-testid={`${testIdPrefix}-phase1-notice`}>
                  Bridge {preview.bridge?.version || "?"} predates v2.3, so the full field set cannot be sent yet.
                  Use <strong>Phase 1 fields only</strong> to create the job now (number + description) — it stays in the
                  queue flagged for a full re-push after the bridge upgrade.
                </div>
              )}
              {preview.phase1_only_pushed && (
                <div className="rounded-md border border-sky-300 bg-sky-50 p-2 text-xs text-sky-900" data-testid={`${testIdPrefix}-phase1-pushed-notice`}>
                  This job already exists in Foundation from a Phase 1 only push — this will fill in the remaining fields.
                </div>
              )}
              <p className="text-xs text-slate-500">
                If the job number already exists in Foundation, the stored procedure will update it.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testIdPrefix}-confirm-cancel`}>Cancel</AlertDialogCancel>
          {preview.phase1_fallback_available && onPhase1 && (
            <button onClick={onPhase1} data-testid={`${testIdPrefix}-confirm-phase1`}
                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-md px-3 py-2 text-sm font-medium">
              Push Phase 1 fields only
            </button>
          )}
          <AlertDialogAction onClick={onConfirm} data-testid={`${testIdPrefix}-confirm-proceed`}
                             className="bg-emerald-600 hover:bg-emerald-700">
            Proceed
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
